import { PostFunction } from "src/utils/axios_connection";
import UrlApi from "src/utils/Url";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";

// The workspace's calls. PostFunction shows the server's message on failure
// and throws; callers keep what is on screen and offer Retry.
const call = (route, data, dispatch, showalert = false, showerror = true) =>
  PostFunction({ Route: route, data, isMultipart: false, showalert, showerror, dispatch });

export const casesApi = {
  // The list and a first load say "did not load" on the page; no toast too.
  list: (params, dispatch) => call(UrlApi.cases.list, params, dispatch, false, false),
  get: (id, dispatch, toast = false) => call(UrlApi.cases.get, { id: Number(id) }, dispatch, false, toast),
  agents: (dispatch) => call(UrlApi.cases.agents, {}, dispatch),
  // No success toast: callers say whether the customer was emailed who is
  // reviewing it (data.emailed). quiet: bulk assign, one summary at the end.
  assign: (id, adminId, dispatch, quiet = false) =>
    call(UrlApi.cases.assign, { id: Number(id), admin_id: adminId }, dispatch, false, quiet ? true : "Couldn’t assign the case. Try again."),
  note: (id, body, dispatch) => call(UrlApi.cases.note, { id: Number(id), body }, dispatch),
  editNote: (noteId, body, dispatch) => call(UrlApi.cases.noteEdit, { note_id: Number(noteId), body }, dispatch, "Note saved.", "Your note wasn’t saved. Nothing changed. Try again."),
  deleteNote: (noteId, dispatch) => call(UrlApi.cases.noteDelete, { note_id: Number(noteId) }, dispatch, "Note deleted.", "Your note wasn’t deleted. Try again."),
  review: (requestDocumentId, decision, reason, dispatch) =>
    call(UrlApi.cases.reviewDocument, { request_document_id: requestDocumentId, decision, ...(reason ? { reason } : {}) }, dispatch),
  requestNewCopies: (id, dispatch) => call(UrlApi.cases.requestNewCopies, { id: Number(id) }, dispatch, true),
  // notify:false changes the status without emailing the customer. The answer
  // says whether an email went out (data.emailed); callers say so in their own
  // toast, so the server's "Status: …" toast is off.
  // Asks the bank whether the application was paid in full; marks every
  // traveller paid when it was. 409 not confirmed, 502 bank unreachable,
  // 503 not set up on this server (the error toast says which).
  markPaid: (id, dispatch) => call("cases/mark-paid", { id: Number(id) }, dispatch),
  // "Travelling together" (API docs/travelling-together.md): staff refunded
  // an inviter's 10% in the bank's portal. The card says what happened.
  refundDone: (discountId, dispatch) => call("cases/refund-done", { discount_id: Number(discountId) }, dispatch, false, false),
  // Builder P (API docs/pay-later.md, docs/on-time-promise.md): money
  // received outside the website (cash, transfer, Whish, OMT) for every
  // unpaid traveller or one, the same steps as the bank's (404 on an older
  // API); and a missed on-time promise: refunded, or not due with a reason.
  // The callers say what happened, so no server toast.
  recordPayment: (body, dispatch) => call("cases/record-payment", body, dispatch, false, false),
  promise: (id, outcome, reason, dispatch) => call("cases/promise", { id: Number(id), outcome, ...(reason ? { reason } : {}) }, dispatch, false, false),
  // No toast on failure: the status dialog says what did not happen.
  // decision: the record saved with Approved or Refused (migration 023).
  status: (id, status, reason, dispatch, notify = true, decision = null) =>
    call(UrlApi.cases.status, { id: Number(id), status, notify, ...(reason ? { reason } : {}), ...(decision ? { decision } : {}) }, dispatch, false, false),
  // The decision record, edited later (or recorded after Approve and send).
  // The dialog says what failed (503 before migration 023).
  decision: (id, outcome, record, dispatch) => call(UrlApi.cases.decision, { id: Number(id), outcome, ...record }, dispatch, false, false),
  // A person on the team called the customer after a refusal. These three
  // say what failed in the caller's own toast (failureWords).
  called: (id, dispatch) => call(UrlApi.cases.decisionCalled, { id: Number(id) }, dispatch, false, false),
  // party: "sponsor" for the sponsor's link (migration 031); the host's otherwise.
  hostResend: (id, dispatch, party = null) => call(UrlApi.cases.hostResend, { id: Number(id), ...(party ? { party } : {}) }, dispatch, false, false),
  // The customer's codes so far are used (the next step stops asking).
  codesUsed: (id, dispatch) => call(UrlApi.cases.codesUsed, { id: Number(id) }, dispatch, false, false),
  // Filing with the embassy (API docs/filing.md): each answers with the
  // whole filing. Callers say what happened in their own toast.
  // quiet: the Start filing dialog says a failure itself, by its button.
  filingStart: (id, dispatch, quiet = false) => call(UrlApi.cases.filingStart, { id: Number(id) }, dispatch, false, !quiet),
  filingStep: (id, stepId, values, dispatch) => call(UrlApi.cases.filingStep, { id: Number(id), step_id: Number(stepId), ...values }, dispatch),
  filingAdd: (id, kind, title, dispatch) => call(UrlApi.cases.filingAdd, { id: Number(id), kind, ...(title ? { title } : {}) }, dispatch),
  filingRemove: (id, stepId, dispatch) => call(UrlApi.cases.filingRemove, { id: Number(id), step_id: Number(stepId) }, dispatch),
  filingEmail: (id, dispatch) => call(UrlApi.cases.filingEmail, { id: Number(id) }, dispatch),
};

// A private file (passport, statement...) as a local blob URL. Staff tokens
// may open any application's files through the user service.
export async function openPrivateFile(path) {
  const user = getLocalStorage(dataLocalStorage.userinfo);
  const response = await fetch(`${UrlApi.baseUrlclient}request/read-private-file/${path}`, {
    headers: { Authorization: `Bearer ${user?.token}` },
  });
  if (!response.ok) throw new Error("This file could not be opened");
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

// The receipt a customer added for a fee they paid outside Safrati, from the
// admin service, as a local blob URL for the file viewer.
export async function openReceipt(stepId) {
  const user = getLocalStorage(dataLocalStorage.userinfo);
  const response = await fetch(`${UrlApi.baseUrl}${UrlApi.cases.feeReceipt(Number(stepId))}`, {
    headers: { Authorization: `Bearer ${user?.token}` },
  });
  if (!response.ok) throw new Error("This receipt could not be opened");
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), type: blob.type };
}

// Copies text, with a fallback for browsers that block the clipboard API.
export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}
